import { useMemo } from 'react';
import type { CalendarDate } from '@internationalized/date';
import { clsx } from 'clsx';
import type { AriaButtonProps } from 'react-aria';
import ArrowLeftIcon from '../../../../../icons/react/icon-arrow-left';
import DisclosureDoubleRightSmallIcon from '../../../../../icons/react/icon-disclosure-double-right-small';
import DisclosureRightSmallIcon from '../../../../../icons/react/icon-disclosure-right-small';
import { HorizontalStack } from '../../../../alignment/horizontal-stack';
import { VerticalStack } from '../../../../alignment/vertical-stack';
import { TextButton } from '../../../../link/text-link/text-button';
import { Typography } from '../../../../typography';
import { DatePickerButton } from '../../date-picker-button';
import type { CalendarMode } from '../util';
import styles from './calendar-header.module.scss';

interface CalendarHeaderProps {
  buttonPropsNext: AriaButtonProps<'button'>;
  buttonPropsPrev: AriaButtonProps<'button'>;
  focusedDate: CalendarDate;
  mode: CalendarMode;
  setFocusedDate: (date: CalendarDate) => void;
  setMode: (mode: CalendarMode) => void;
  yearSelectOptionCount: number;
}

export function CalendarHeader({
  buttonPropsNext,
  buttonPropsPrev,
  focusedDate,
  mode,
  setFocusedDate,
  setMode,
  yearSelectOptionCount,
}: CalendarHeaderProps) {
  const formattedMonthName = useMemo(
    () => new Date(focusedDate.year, focusedDate.month - 1, 1).toLocaleString('en-US', { month: 'long' }),
    [focusedDate.month, focusedDate.year],
  );
  const startYear = focusedDate.year - yearSelectOptionCount + 1;

  return (
    <VerticalStack className={styles.calendarHeader}>
      <div className={styles.actionBar}>
        {mode === 'day' ? (
          <DatePickerButton
            className={clsx(styles.arrowButton, styles.rotate)}
            type="button"
            {...buttonPropsPrev}
            onPress={() => {
              setFocusedDate(focusedDate.subtract({ years: 1 }));
            }}
          >
            <DisclosureDoubleRightSmallIcon aria-hidden width={16} />
          </DatePickerButton>
        ) : (
          <button
            aria-label="Back to calendar"
            className={styles.arrowButton}
            onClick={() => {
              setMode('day');
            }}
            type="button"
          >
            <ArrowLeftIcon aria-hidden width={16} />
          </button>
        )}

        <DatePickerButton
          className={clsx(styles.arrowButton, styles.rotate, mode === 'month' && styles.isUnused)}
          type="button"
          {...buttonPropsPrev}
          onPress={(e) => {
            if (mode === 'day') {
              buttonPropsPrev.onPress?.(e);
            } else {
              setFocusedDate(focusedDate.subtract({ years: yearSelectOptionCount }));
            }
          }}
        >
          <DisclosureRightSmallIcon aria-hidden width={16} />
        </DatePickerButton>

        <HorizontalStack align="center" blockAlign="center" className={styles.titleContainer} gap={1} hidden={mode !== 'day'} wrap="nowrap">
          <TextButton
            onClick={() => {
              setMode('month');
            }}
            size="small"
            underline="hover"
          >
            {formattedMonthName}
          </TextButton>

          <TextButton
            onClick={() => {
              setMode('year');
            }}
            size="small"
            underline="hover"
          >
            {focusedDate.year}
          </TextButton>
        </HorizontalStack>

        {mode === 'month' ? (
          <Typography className={styles.titleContainer} lineHeight="1.5rem" size="0.875rem">
            Month
          </Typography>
        ) : null}

        {mode === 'year' ? (
          <Typography className={styles.titleContainer} lineHeight="1.5rem" size="0.875rem">
            {`${startYear}-${focusedDate.year}`}
          </Typography>
        ) : null}

        <DatePickerButton
          className={clsx(styles.arrowButton, mode === 'month' && styles.isUnused)}
          type="button"
          {...buttonPropsNext}
          onPress={(e) => {
            if (mode === 'day') {
              buttonPropsNext.onPress?.(e);
            } else {
              setFocusedDate(focusedDate.add({ years: yearSelectOptionCount }));
            }
          }}
        >
          <DisclosureRightSmallIcon aria-hidden width={16} />
        </DatePickerButton>

        <DatePickerButton
          className={clsx(styles.arrowButton, mode !== 'day' && styles.isUnused)}
          type="button"
          {...buttonPropsPrev}
          onPress={() => {
            setFocusedDate(focusedDate.add({ years: 1 }));
          }}
        >
          <DisclosureDoubleRightSmallIcon aria-hidden width={16} />
        </DatePickerButton>
      </div>

      <hr className={styles.separator} />
    </VerticalStack>
  );
}
